import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Navbar } from '../navbar/navbar';
import { Footer } from '../footer/footer';
import { AuthContextService } from '../shared/auth-context.service';

@Component({
  selector: 'app-appointment-scheduled',
  imports: [CommonModule, Navbar, Footer],
  templateUrl: './appointment-scheduled.html',
  styleUrl: './appointment-scheduled.css'
})
export class AppointmentScheduled implements OnInit {
  appointmentDate = 'October 21, 2024';
  appointmentTime = '10:00 AM';
  appointmentProvider = '';
  appointmentData: any = null;
  showNoShowPolicy = false;

  constructor(
    private router: Router,
    private auth: AuthContextService
  ) {}

  private pushDataLayerEvent(eventName: string, payload: Record<string, unknown> = {}): void {
    const normalizedEventName = String(eventName || '').trim();
    if (!normalizedEventName) return;
    try {
      const dataLayer = (window as any).dataLayer = (window as any).dataLayer || [];
      dataLayer.push({
        event: normalizedEventName,
        ...payload
      });
    } catch {}
  }

  private clearTransientStorage(): void {
    try {
      const sessionKeysToRemove = [
        'flow.bypass',
        'flow.canSeeAvailableSlots',
        'flow.canSeeDetailsMe',
        'flow.scheduledOK',
        'appointmentData',
        'appointmentPatientId',
        'flow.wl.entry',
        'flow.wl.success'
      ];
      for (const key of sessionKeysToRemove) {
        try { sessionStorage.removeItem(key); } catch {}
      }
    } catch {}

    try {
      const localKeysToRemove = [
        'sheet.BasicCache',
        'sheet.Date_received',
        'sheet.Time_received',
        'sheet.RowNumber',
        'sheet.RowExisting',
        'sheet.Row_number',
        'RowNumber',
        'Row_number',
        'findAppointment.modalInfo'
      ];
      for (const key of localKeysToRemove) {
        try { localStorage.removeItem(key); } catch {}
      }
    } catch {}
  }

  ngOnInit() {
   
    try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' as any }); } catch { window.scrollTo(0, 0); }

    try {
      const ok = sessionStorage.getItem('flow.scheduledOK') === '1';
      if (!ok) {
        // Refresh after the booking is done: the flow flag is already gone, so drop the cached sheet data too.
        try { localStorage.removeItem('sheet.BasicCache'); } catch {}
        this.router.navigate(['/scheduler']);
        return;
      }
    } catch {}

    this.pushDataLayerEvent('ols_appointment_form_submission');
    
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      this.appointmentData = navigation.extras.state['appointmentData'];
      
      if (this.appointmentData) {
 
        if (this.appointmentData.date) {
          this.appointmentDate = this.formatDate(this.appointmentData.date);
        }
        if (this.appointmentData.time) {
          this.appointmentTime = this.appointmentData.time;
        }
        if (this.appointmentData.provider) {
          this.appointmentProvider = String(this.appointmentData.provider || '').trim();
        }
      }
    }
   
    const storedData = sessionStorage.getItem('appointmentData');
    if (storedData && !this.appointmentData) {
      this.appointmentData = JSON.parse(storedData);
      if (this.appointmentData.date) {
        this.appointmentDate = this.formatDate(this.appointmentData.date);
      }
      if (this.appointmentData.time) {
        this.appointmentTime = this.appointmentData.time;
      }
      if (this.appointmentData.provider) {
        this.appointmentProvider = String(this.appointmentData.provider || '').trim();
      }
    }


    let selection = this.auth.getAppointmentSelection();
    if (!this.appointmentProvider) {
      this.appointmentProvider = String(selection?.provider || '').trim();
    }
    if (!this.appointmentData) {
      if (selection) {
        this.appointmentDate = this.formatDate(selection.date || this.appointmentDate);
        this.appointmentTime = selection.time || this.appointmentTime;
      }
    }

    // Ensure the displayed time includes the correct time zone
    try {
      const patient = this.auth.getPatientInfo();
      const stateFullOrAbbrev = (selection?.state || patient?.state || '').toString();
      const stateAbbrev = this.toStateAbbrev(stateFullOrAbbrev);
      const dateRef: string | null = selection?.startdatetime || this.appointmentData?.startdatetime || null;
      const hasZone = /(\s)(ET|CT|MT|PT|EST|EDT|CST|CDT|MST|MDT|PST|PDT)\b/.test(this.appointmentTime);
      if (!hasZone) {
        const tzAbbrev = this.getTimeZoneAbbrevForState(dateRef, stateAbbrev);
        this.appointmentTime = `${this.appointmentTime} ${tzAbbrev}`.trim();
      }
    } catch {}
    // Shown for all patients on the confirmation page, regardless of how soon the appointment is.
    this.showNoShowPolicy = true;


    this.clearTransientStorage();
    this.auth.clearAll();
    try {
      history.pushState(null, '', location.href);
      window.onpopstate = () => {
        history.go(1);
      };
    } catch {}
  }

  private formatDate(dateInput: string): string {
    try {
      const date = new Date(dateInput);
      const options: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {

      return dateInput || 'October 21, 2024';
    }
  }

  private toStateAbbrev(input: string): string {
    if (!input) return 'NC';
    const map: Record<string, string> = {
      'NC': 'North Carolina', 'NY': 'New York', 'FL': 'Florida', 'GA': 'Georgia',
      'TN': 'Tennessee', 'MO': 'Missouri', 'AR': 'Arkansas', 'IL': 'Illinois',
      'OK': 'Oklahoma', 'TX': 'Texas', 'AZ': 'Arizona', 'WA': 'Washington',
      'OR': 'Oregon', 'CA': 'California'
    };
    const trimmed = input.trim();
    if (trimmed.length === 2) return trimmed.toUpperCase();
    for (const [abbr, full] of Object.entries(map)) {
      if (full.toLowerCase() === trimmed.toLowerCase()) return abbr;
    }
    return 'NC';
  }

  private getTimeZoneRegionForState(stateAbbrev: string): string {
    const tzMap: Record<string, string> = {
      'NC': 'America/New_York', 'NY': 'America/New_York', 'FL': 'America/New_York', 'GA': 'America/New_York',
      'TN': 'America/Chicago', 'MO': 'America/Chicago', 'AR': 'America/Chicago', 'IL': 'America/Chicago',
      'OK': 'America/Chicago', 'TX': 'America/Chicago', 'AZ': 'America/Phoenix', 'WA': 'America/Los_Angeles',
      'OR': 'America/Los_Angeles', 'CA': 'America/Los_Angeles'
    };
    return tzMap[stateAbbrev] || 'America/New_York';
  }

  private getTimeZoneAbbrevForState(dateInput: string | Date | null, stateAbbrev: string): string {
    try {
      const tz = this.getTimeZoneRegionForState(stateAbbrev);
      const d = dateInput ? new Date(dateInput) : new Date();
      if (isNaN(d.getTime())) return 'ET';
      const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' });
      const parts = fmt.formatToParts(d);
      const name = parts.find(p => p.type === 'timeZoneName')?.value || '';
      if (/GMT[+-]\d+/.test(name)) {
        const generic: Record<string, string> = {
          'America/New_York': 'ET',
          'America/Chicago': 'CT',
          'America/Denver': 'MT',
          'America/Phoenix': 'MST',
          'America/Los_Angeles': 'PT'
        };
        return generic[tz] || name;
      }
      return name;
    } catch {
      return 'ET';
    }
  }

  clearContext() {
    this.auth.clearAll();
  }
}
